import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signOut, sendEmailVerification } from "firebase/auth";
import { auth } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { LeafMark } from "./LeafMark";

function TenantLogo({ logoUrl, size = 28 }: { logoUrl?: string; size?: number }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="shrink-0 rounded-lg object-contain"
        style={{ width: size, height: size }}
      />
    );
  }
  return <LeafMark size={size} />;
}

/**
 * Barra superior reusable para vistas dentro de tenant.
 * Muestra el tenant actual + acceso a perfil/admin/ambassador según role.
 */
export function TopBar() {
  const nav = useNavigate();
  const { user, claims } = useAuth();
  const { tenant } = useTenant();
  const [menuOpen, setMenuOpen] = useState(false);

  const firstName = user?.displayName?.split(" ")[0] ?? user?.email?.split("@")[0] ?? "";
  const isAmbassador = claims.role === "ambassador";
  const isTenantAdmin = claims.role === "tenant_admin";

  const handleLogout = async () => {
    setMenuOpen(false);
    await signOut(auth);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-leaf-100 bg-cream/95 backdrop-blur-md">
      {user && !user.isAnonymous && user.email && !user.emailVerified && (
        <EmailVerifyBanner />
      )}
      <div className="mx-auto flex max-w-md items-center justify-between gap-3 px-5 py-3">
        <Link to="/" className="flex items-center gap-2.5 min-w-0">
          <TenantLogo logoUrl={tenant?.theme.logoUrl} size={28} />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-leaf-600">Fideliza</p>
            <p className="truncate font-display text-base font-semibold text-leaf-950">
              {tenant?.name ?? "Cargando…"}
            </p>
          </div>
        </Link>

        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-leaf-100 font-display text-sm font-semibold text-leaf-800"
            aria-label="Menú"
          >
            {firstName.charAt(0).toUpperCase() || "?"}
          </button>

          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
                aria-hidden
              />
              <div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-leaf-100 bg-white shadow-xl">
                <div className="border-b border-leaf-100 px-4 py-3">
                  <p className="truncate text-sm font-semibold text-leaf-950">
                    {firstName || "Tu cuenta"}
                  </p>
                  <p className="truncate text-xs text-leaf-600">
                    {user?.email ?? "Cuenta anónima"}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-leaf-500">
                    {claims.role ?? "—"}
                  </p>
                </div>
                <MenuItem
                  label="Mi perfil"
                  onClick={() => {
                    setMenuOpen(false);
                    nav("/perfil");
                  }}
                />
                {isAmbassador && (
                  <>
                    <MenuItem
                      label="Panel embajador"
                      onClick={() => {
                        setMenuOpen(false);
                        nav("/embajador");
                      }}
                    />
                    <MenuItem
                      label="Mis codigos"
                      onClick={() => {
                        setMenuOpen(false);
                        nav("/embajador#codigos");
                      }}
                    />
                  </>
                )}
                {claims.role === "staff" && (
                  <>
                    <MenuItem
                      label="Cobrar venta"
                      onClick={() => {
                        setMenuOpen(false);
                        nav("/staff/cobro");
                      }}
                    />
                    <MenuItem
                      label="Mis ventas hoy"
                      onClick={() => {
                        setMenuOpen(false);
                        nav("/staff/cobro?tab=today");
                      }}
                    />
                  </>
                )}
                {isTenantAdmin && (
                  <>
                    <MenuItem
                      label="Panel admin"
                      onClick={() => {
                        setMenuOpen(false);
                        nav("/admin");
                      }}
                    />
                    <MenuItem
                      label="Ventas hoy"
                      onClick={() => {
                        setMenuOpen(false);
                        nav("/admin?tab=sales");
                      }}
                    />
                    <MenuItem
                      label="Ajustes del comercio"
                      onClick={() => {
                        setMenuOpen(false);
                        nav("/admin?tab=settings");
                      }}
                    />
                  </>
                )}
                {!isAmbassador && claims.role === "customer" && (
                  <MenuItem
                    label="Postular a embajador"
                    onClick={() => {
                      setMenuOpen(false);
                      nav("/postular-embajador");
                    }}
                  />
                )}
                <div className="border-t border-leaf-100">
                  <MenuItem label="Cerrar sesión" onClick={handleLogout} variant="danger" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function EmailVerifyBanner() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    if (!auth.currentUser) return;
    setBusy(true);
    try {
      await sendEmailVerification(auth.currentUser);
      setSent(true);
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-amber-100 px-4 py-2 text-center text-xs text-amber-900">
      {sent ? (
        <span>Te enviamos un nuevo email. Revisa tu bandeja.</span>
      ) : (
        <span>
          Verifica tu email para asegurar tu cuenta.{" "}
          <button onClick={resend} disabled={busy} className="font-semibold underline">
            {busy ? "Enviando…" : "Reenviar"}
          </button>
        </span>
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  variant = "default"
}: {
  label: string;
  onClick: () => void;
  variant?: "default" | "danger";
}) {
  return (
    <button
      onClick={onClick}
      className={`block w-full px-4 py-3 text-left text-sm transition ${
        variant === "danger"
          ? "text-red-700 hover:bg-red-50"
          : "text-leaf-900 hover:bg-leaf-50"
      }`}
    >
      {label}
    </button>
  );
}
